import { Component } from '@theme/component';

const FEEDBACK_MS = 2000;
// Below this, an AbortError cannot be a human dismissing the native share sheet.
const SHEET_DISMISS_MS = 300;

/**
 * Share button: Web Share API with clipboard fallback and GA4 customer event.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} shareButton - Visible share control.
 * @property {HTMLElement} statusRegion - Live region for copy feedback.
 *
 * @extends {Component<Refs>}
 */
export class ProductShareButtonComponent extends Component {
	requiredRefs = ['shareButton', 'statusRegion'];

	/** @type {ReturnType<typeof setTimeout> | null} */
	#feedbackTimeout = null;

	disconnectedCallback() {
		this.#clearFeedbackTimeout();
		super.disconnectedCallback();
	}

	/**
	 * Handles share button click.
	 * @param {Event} event
	 */
	handleShare = async (event) => {
		event.preventDefault();

		const title = this.dataset.shareTitle ?? '';
		const url = this.dataset.shareUrl ?? '';

		if (!url) return;

		const shareData = { title, url };

		if (typeof navigator.share === 'function' && (!navigator.canShare || navigator.canShare(shareData))) {
			const startedAt = performance.now();

			try {
				await navigator.share(shareData);
				this.#track('native', url);
				return;
			} catch (error) {
				// AbortError means either the user dismissed the share sheet or the platform has
				// no share targets at all. Only a human takes longer than SHEET_DISMISS_MS, so a
				// faster abort means the sheet never opened and we must fall back.
				const dismissedByUser =
					error instanceof Error && error.name === 'AbortError' && performance.now() - startedAt >= SHEET_DISMISS_MS;

				if (dismissedByUser) return;
			}
		}

		if (await this.#copyToClipboard(url)) {
			this.#showFeedback('copied');
			this.#track('copy', url);
		} else {
			this.#showFeedback('error');
		}
	};

	/**
	 * @param {'copied' | 'error'} state
	 */
	#showFeedback(state) {
		const { shareButton, statusRegion } = this.refs;

		const label =
			state === 'copied'
				? this.dataset.labelCopied ?? 'Link copied'
				: this.dataset.labelCopyFailed ?? 'Could not copy link';

		statusRegion.textContent = label;
		shareButton.classList.toggle('product-share-button--feedback', state === 'copied');
		shareButton.classList.toggle('product-share-button--error', state === 'error');

		this.#clearFeedbackTimeout();
		this.#feedbackTimeout = setTimeout(() => {
			statusRegion.textContent = '';
			shareButton.classList.remove('product-share-button--feedback', 'product-share-button--error');
			this.#feedbackTimeout = null;
		}, FEEDBACK_MS);
	}

	#clearFeedbackTimeout() {
		if (this.#feedbackTimeout == null) return;
		clearTimeout(this.#feedbackTimeout);
		this.#feedbackTimeout = null;
	}

	/**
	 * @param {string} url
	 * @returns {Promise<boolean>}
	 */
	async #copyToClipboard(url) {
		try {
			if (navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(url);
				return true;
			}
		} catch {
			// Fall through to execCommand fallback.
		}

		try {
			const textarea = document.createElement('textarea');
			textarea.value = url;
			textarea.setAttribute('readonly', '');
			textarea.style.position = 'fixed';
			textarea.style.left = '-9999px';
			document.body.appendChild(textarea);
			textarea.select();
			const copied = document.execCommand('copy');
			textarea.remove();
			return copied;
		} catch {
			return false;
		}
	}

	/**
	 * @param {'native' | 'copy'} method
	 * @param {string} productUrl
	 */
	#track(method, productUrl) {
		if (typeof Shopify === 'undefined' || !Shopify.analytics?.publish) return;

		const eventData = {
			method,
			productUrl,
			productId: this.dataset.productId,
			productHandle: this.dataset.productHandle,
		};

		Shopify.analytics.publish('product_share', eventData);

		window.dataLayer?.push({
			event: 'product_share',
			share_method: method,
			product_url: productUrl,
			product_id: eventData.productId,
			product_handle: eventData.productHandle,
		});
	}
}

if (!customElements.get('product-share-button')) {
	customElements.define('product-share-button', ProductShareButtonComponent);
}
